/**
 * Week arithmetic on **local calendar dates**, for the week view.
 *
 * A deliberate parallel to the server's `week-window.ts` rather than a shared
 * package: the two need the same *rules* but for different reasons — the server
 * turns a week into a SQL bound, the client turns it into tabs and links — and a
 * shared date package between a NestJS app and a Vite bundle is more coupling than
 * ~60 lines of arithmetic is worth. Both are pure and both are tested, so a
 * divergence shows up as a failing test rather than as a wrong screen.
 *
 * All dates are `YYYY-MM-DD` with **no zone**. Iteration is in UTC deliberately:
 * stepping a *local* midnight by 24 hours lands on the same day again across a
 * spring-forward, which would duplicate a tab and drop another.
 */

const DAYS_IN_WEEK = 7;

/** An inclusive Monday-to-Sunday range of local calendar dates. */
export interface Week {
  /** Monday, `YYYY-MM-DD`. */
  from: string;
  /** Sunday, `YYYY-MM-DD`. */
  to: string;
}

/**
 * The Monday-based week containing `date`.
 *
 * Monday-first because this is a European app: the weekend belongs at the end of
 * the week rather than split across both edges, which also keeps Friday and
 * Saturday — the two nights most events land on — adjacent in the tab strip.
 */
export function weekOf(date: string): Week {
  const cursor = parseDate(date);

  /*
   * getUTCDay() is 0 on Sunday, so Sunday is six days *after* its Monday rather
   * than one day before the next. The naive `day - 1` shifts every Sunday into
   * the previous week — invisible in most test data, because so little is
   * scheduled on a Sunday.
   */
  const daysSinceMonday =
    (cursor.getUTCDay() + DAYS_IN_WEEK - 1) % DAYS_IN_WEEK;

  cursor.setUTCDate(cursor.getUTCDate() - daysSinceMonday);
  const from = format(cursor);

  cursor.setUTCDate(cursor.getUTCDate() + DAYS_IN_WEEK - 1);
  return { from, to: format(cursor) };
}

/** The week `offset` weeks away. Negative goes back. */
export function shiftWeeks(date: string, offset: number): Week {
  const cursor = parseDate(date);
  cursor.setUTCDate(cursor.getUTCDate() + offset * DAYS_IN_WEEK);
  return weekOf(format(cursor));
}

/**
 * The seven dates of a week, in order — the day tabs.
 *
 * Always seven, including days with nothing on: a strip that hid empty days would
 * change width as you navigate, and "nothing on Tuesday" is worth showing.
 */
export function datesIn(week: Week): string[] {
  const dates: string[] = [];
  const cursor = parseDate(week.from);
  const end = parseDate(week.to);

  while (cursor.getTime() <= end.getTime()) {
    dates.push(format(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

/** ISO-8601 week number, for the "Week 31" heading. */
export function isoWeekNumber(date: string): number {
  /*
   * ISO's rule — "week 1 contains the year's first Thursday" — is equivalent to
   * counting from 1 January of the year containing *this week's* Thursday. Working
   * from the Thursday is what makes the year boundary fall out correctly:
   * 2027-01-01 is a Friday, so it belongs to week 53 of 2026.
   */
  const thursday = parseDate(weekOf(date).from);
  thursday.setUTCDate(thursday.getUTCDate() + 3);

  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysIn = (thursday.getTime() - yearStart) / msPerDay;

  return Math.floor(daysIn / DAYS_IN_WEEK) + 1;
}

/**
 * Today's date **in the area's timezone**, `YYYY-MM-DD`.
 *
 * Not the viewer's today. The week view opens on the current week *there*, so a
 * viewer reading from another zone sees the same week as someone standing in the
 * city — which is the whole premise of area-relative bucketing.
 */
export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * Which local day an instant falls on, in the area's zone.
 *
 * The client-side half of the bucketing rule: the server filters a week, and this
 * decides which day column each event lands in. Both must agree, and both use the
 * **area's** zone — an event at 23:00 Thursday sits under Thursday even for a
 * viewer whose own clock already says Friday.
 */
export function localDayOf(instant: string, timeZone: string): string {
  return todayIn(timeZone, new Date(instant));
}

/** Shape check plus a real-calendar check, so `2026-02-30` is rejected. */
export function isValidDate(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  // Round-trips only if the date exists — Date rolls 02-30 forward to 03-02.
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === date
  );
}

function parseDate(date: string): Date {
  if (!isValidDate(date)) {
    throw new Error(`Expected YYYY-MM-DD, got "${date}"`);
  }
  return new Date(`${date}T00:00:00Z`);
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}
